/**
 * features/finance/RemindersPanel.tsx
 * ------------------------------------------------------------------
 * DID THE REMINDER ARRIVE?
 *
 * The honest answer has three parts, and this screen refuses to blur
 * them together, because blurring them is what let the school send 905
 * reminders and reach 57 families while every screen reported success.
 *
 *   WHAT WE SENT — the log, one row per attempt.
 *   WHAT THE MAIL SERVER TOOK — "accepted". Gmail can accept a message
 *     and bounce it minutes later into the bursar's inbox, where nothing
 *     in this portal can see it. Accepted is not delivered.
 *   WHETHER THE PARENT READ IT — unknowable. There is no open tracking
 *     and no read receipt, so no number is shown. Inventing one would be
 *     worse than the gap.
 *
 * What IS knowable, and what actually went wrong here, is whether an
 * address can receive mail at all. That check runs server-side against
 * real DNS, and it is the panel worth reading first.
 * ------------------------------------------------------------------ */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  MailCheck, MailX, MailQuestion, AlertTriangle, RefreshCw, ShieldCheck, Search, Inbox,
  History, FileDown,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { db, functions } from '@/lib/firebase';
import {
  summarise, byDay, problems, isDeliveryRecord, isSentinel, summariseSentinels,
  classifyAddress, type DeliveryEntry, type SentRecord,
} from '@/lib/reminderDelivery';
import {
  SoftCard, PanelHeader, StatChip, glassFieldClass, btnCsv, btnPdf, AccentButton,
} from '@/features/finance/ui';
import { buildMissingContactPacks, packFileName } from '@/lib/missingContacts';
import { buildMissingContactsPdf } from '@/lib/missingContactsPdf';
import { loadLogoDataUrl } from '@/lib/loadLogo';

const REMINDER_LOG = 'financeReminderLog';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const when = (iso: string) => (iso ? iso.slice(0, 16).replace('T', ' ') : '—');

interface ReachRowDto {
  studentId: string; studentName: string; className: string;
  current: string; outstanding: number;
  email?: string; who?: string; field?: string;
  /* Only on the unreachable rows, and only what a class teacher needs to
     go and ask: no money travels with these. */
  classId?: string; classTeacher?: string; admissionNumber?: string;
  guardianNames?: string[]; phones?: string[]; heldEmail?: string;
}
interface ReachDto {
  checkedAt: string;
  report: Record<'reachable' | 'recoverable' | 'unreachable', { pupils: number; outstanding: number }>;
  deadDomains: { domain: string; pupils: number }[];
  recoverable: ReachRowDto[];
  unreachable: ReachRowDto[];
  recoverableTotal: number;
  unreachableTotal: number;
  /** Addresses a real bounce condemned, as opposed to a dead domain. */
  bouncedAddresses?: number;
}

/** Straight off the wire: this collection holds delivery records AND the
 *  sweep's sentinels, which share no fields beyond `to` and `kind`. */
type RawRow = DeliveryEntry & {
  sentAt?: { toDate?: () => Date; seconds?: number };
  invoiceId?: string;
  instalment?: string | number;
};

function tsToIso(ts: RawRow['sentAt']): string {
  if (!ts) return '';
  if (typeof ts.toDate === 'function') return ts.toDate().toISOString();
  if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000).toISOString();
  return '';
}

const OUTCOME_LABEL: Record<string, string> = {
  sent: 'Accepted by the mail server',
  rejected: 'Refused',
  failed: 'Could not be sent',
  'no-address': 'No address on file',
};

export default function RemindersPanel() {
  const [rows, setRows] = useState<RawRow[] | null>(null);
  const [logErr, setLogErr] = useState('');
  const [reach, setReach] = useState<ReachDto | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkErr, setCheckErr] = useState('');
  const [packing, setPacking] = useState(false);
  const [packNote, setPackNote] = useState('');

  const [search, setSearch] = useState('');

  /* READ BOTH KINDS OF DOCUMENT. Ordering by `atIso` at the query would
     drop every sentinel, because Firestore omits documents missing the
     ordered field — and the sentinels are the ONLY record of the 409
     reminders sent before delivery logging existed. Ordering by a field
     half the collection does not have is how this screen came to tell a
     school that had chased 905 families the day before that nothing had
     been sent yet. Sorted below instead. */
  useEffect(() => onSnapshot(
    query(collection(db, REMINDER_LOG), limit(3000)),
    (s) => { setLogErr(''); setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as RawRow)); },
    /* A failed read is not an empty log. Reporting it as one would repeat
       the exact mistake this screen exists to correct. */
    (e) => { setLogErr(e.message || 'The reminder log could not be read.'); setRows([]); },
  ), []);

  const runCheck = useCallback(async () => {
    setChecking(true); setCheckErr('');
    try {
      const fn = httpsCallable(functions, 'checkReminderReach');
      setReach(((await fn({})).data) as ReachDto);
    } catch (e) {
      setCheckErr(e instanceof Error ? e.message : 'The check could not be run.');
    } finally {
      setChecking(false);
    }
  }, []);

  const log = useMemo(
    () => (rows ?? []).filter(isDeliveryRecord) as DeliveryEntry[],
    [rows],
  );

  /* The sweep's sentinels, normalised. They carry a Firestore Timestamp
     rather than an ISO string, because they were written years before
     anything read them back. */
  const legacy = useMemo<SentRecord[]>(() => (rows ?? [])
    .filter(isSentinel)
    .map((r) => ({
      id: r.id, invoiceId: r.invoiceId, instalment: r.instalment,
      kind: r.kind as SentRecord['kind'], to: r.to,
      sentAtIso: tsToIso(r.sentAt),
    }))
    .sort((a, b) => b.sentAtIso.localeCompare(a.sentAtIso)), [rows]);

  const stats = useMemo(() => summarise(log), [log]);
  const days = useMemo(() => byDay(log).slice(0, 14), [log]);
  const old = useMemo(() => summariseSentinels(legacy), [legacy]);
  const trouble = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = problems(log);
    return q
      ? list.filter((r) => `${r.studentName ?? ''} ${r.to ?? ''} ${r.invoiceNo ?? ''}`
        .toLowerCase().includes(q))
      : list;
  }, [log, search]);

  function exportReach() {
    if (!reach) return;
    const rows = [
      ['Band', 'Pupil', 'Class', 'Address in use', 'Address that works', 'Whose', 'Field', 'Outstanding'],
      ...reach.recoverable.map((r) => ['Recoverable', r.studentName, r.className,
        r.current, r.email ?? '', r.who ?? '', r.field ?? '', r.outstanding.toFixed(2)]),
      ...reach.unreachable.map((r) => ['Unreachable', r.studentName, r.className,
        r.current, '', '', '', r.outstanding.toFixed(2)]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `reminder-reach-${reach.checkedAt.slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * ONE SHEET PER CLASS TEACHER, ZIPPED.
   *
   * The families left here have never given the school an address, and no
   * amount of code will produce one. The only way forward is for somebody who
   * sees the child every day to ask, so this exists to be forwarded: one PDF
   * per class, addressed to that class's teacher, with a blank line after
   * each pupil to write the answer on.
   *
   * JSZip is imported on demand, as in BulkPayslips, so a screen most people
   * open to read a delivery log does not carry a zip library to do it.
   */
  async function downloadPacks() {
    if (!reach || packing) return;
    setPacking(true);
    setPackNote('');
    try {
      const packs = buildMissingContactPacks(reach.unreachable);
      if (!packs.length) { setPackNote('Nothing to send — every family has a usable address.'); return; }
      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();
      const logo = await loadLogoDataUrl();
      for (const pack of packs) {
        const pdf = buildMissingContactsPdf(pack, { logoDataUrl: logo, checkedAt: reach.checkedAt });
        zip.file(packFileName(pack), pdf.output('blob'));
      }
      const out = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(out);
      const a = document.createElement('a');
      a.href = url;
      a.download = `LKC_missing_contacts_by_class_${reach.checkedAt.slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const pupils = packs.reduce((n, x) => n + x.pupils.length, 0);
      setPackNote(`${packs.length} sheet${packs.length > 1 ? 's' : ''}, ${pupils} pupil${pupils > 1 ? 's' : ''}.`);
    } catch (e) {
      setPackNote((e as Error).message);
    } finally {
      setPacking(false);
    }
  }

  if (rows === null) return <div className="p-6 flex justify-center"><Spinner label="Loading…" /></div>;

  const rep = reach?.report;

  return (
    <div className="space-y-6">

      {/* ---- what we sent -------------------------------------- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatChip tone="navy" icon={MailCheck} value={String(stats.accepted)}
          label="Accepted by the mail server" />
        <StatChip tone={stats.rejected ? 'rose' : 'slate'} icon={MailX}
          value={String(stats.rejected)} label="Refused at sending" />
        <StatChip tone={stats.failed ? 'amber' : 'slate'} icon={AlertTriangle}
          value={String(stats.failed)} label="Could not be sent" />
        <StatChip tone={stats.noAddress ? 'amber' : 'slate'} icon={MailQuestion}
          value={String(stats.noAddress)} label="No address on file" />
      </div>

      {logErr && (
        <SoftCard hover={false} className="border-l-4 border-red-500">
          <p className="text-sm font-medium text-red-700 dark:text-red-300">
            The reminder log could not be read — this is not an empty log.
          </p>
          <p className="text-[12px] text-gray-500 mt-1">{logErr}</p>
        </SoftCard>
      )}

      {/* ---- the sends that predate delivery logging ------------ */}
      {old.total > 0 && (
        <SoftCard pad="p-0">
          <PanelHeader
            icon={History} tone="gold"
            title={`${old.total} reminders sent before delivery logging existed`}
            subtitle={old.earliest
              ? `${old.earliest.slice(0, 10)} to ${old.latest.slice(0, 10)} — recipient known, outcome not recorded`
              : 'Recipient known, outcome not recorded'}
          />
          <div className="p-4">
            <p className="text-[12px] text-gray-600 dark:text-gray-300 mb-3">
              These come from the sweep's own records, which have always noted that a reminder
              went out and to what address, but never what became of it. So none of them can be
              called delivered — and the addresses say most were not. Everything sent from now on
              is recorded properly above.
            </p>

            {old.toDeadAddress > 0 && (
              <div className="rounded-lg border border-red-200 dark:border-red-900/60 bg-red-50/60 dark:bg-red-950/20 p-3 mb-3">
                <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                  {old.toDeadAddress} of these {old.total} went to an address that cannot receive mail.
                </p>
                <p className="text-[12px] text-gray-600 dark:text-gray-300">
                  Not "probably bounced" — those domains have no mail exchanger, so nothing sent
                  to them arrives. Those families have not been chased, whatever the log says.
                  {old.deadDomains.map((d) => (
                    <span key={d.domain}
                      className="inline-block font-mono text-[11px] px-2 py-0.5 ml-1.5 rounded bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200">
                      {d.domain} ({d.sends})
                    </span>
                  ))}
                </p>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                    <th className="py-2 pr-2 font-semibold">Sent to</th>
                    <th className="px-2 font-semibold">Could it arrive?</th>
                    <th className="px-2 font-semibold">Invoice</th>
                    <th className="px-2 pr-2 font-semibold">When</th>
                  </tr>
                </thead>
                <tbody>
                  {legacy.slice(0, 100).map((r) => {
                    const v = classifyAddress(r.to);
                    return (
                      <tr key={r.id} className="border-b border-gray-50 dark:border-gray-800">
                        <td className="py-2 pr-2 font-mono text-xs text-gray-600 dark:text-gray-300">
                          {r.to}
                        </td>
                        <td className="px-2">
                          {v.status === 'ok' ? (
                            <span className="text-xs text-gray-500">Possibly — not recorded</span>
                          ) : (
                            <span className="text-xs font-medium text-red-700 dark:text-red-300">
                              No — {v.reason}
                            </span>
                          )}
                        </td>
                        <td className="px-2 font-mono text-[11px] text-gray-400 truncate max-w-[220px]">
                          {r.invoiceId}
                        </td>
                        <td className="px-2 pr-2 text-xs text-gray-500 tabular-nums">
                          {when(r.sentAtIso)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {legacy.length > 100 && (
                <p className="text-[12px] text-gray-500 p-3">and {legacy.length - 100} more.</p>
              )}
            </div>
          </div>
        </SoftCard>
      )}

      {/* The distinction the whole screen turns on. */}
      <SoftCard hover={false} className="border-l-4 border-amber-400">
        <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">
          “Accepted” is not “delivered”, and neither is “read”.
        </p>
        <p className="text-[12px] text-gray-600 dark:text-gray-300">
          A mail server can accept a message and bounce it minutes later to the sender's inbox,
          where nothing in this portal can see it — that is how 905 reminders were reported as
          sent. There is no open tracking here, so the number of parents who actually read a
          reminder is not known and is not shown. What <em>can</em> be established beforehand is
          whether an address is able to receive mail at all. That is the check below.
        </p>
        <p className="text-[11px] text-gray-400 mt-2">
          Last attempt: {when(stats.lastAttemptAt)} · showing the most recent 1,000 attempts
        </p>
      </SoftCard>

      {/* ---- can we reach them at all --------------------------- */}
      <SoftCard pad="p-0">
        <PanelHeader
          icon={ShieldCheck} tone="navy" title="Can these families be reached?"
          subtitle="Checks every address on file against real DNS — a domain with no mail exchanger cannot receive anything"
          right={(
            <div className="flex items-center gap-2">
              {reach && (
                <button onClick={exportReach} className={btnCsv}>Export CSV</button>
              )}
              {reach && reach.unreachable.length > 0 && (
                <button onClick={downloadPacks} className={btnPdf} disabled={packing}>
                  <FileDown size={14} />
                  {packing ? 'Building…' : 'Class sheets (ZIP)'}
                </button>
              )}
              <AccentButton icon={RefreshCw} onClick={runCheck} disabled={checking}>
                {checking ? 'Checking…' : reach ? 'Check again' : 'Run the check'}
              </AccentButton>
            </div>
          )}
        />

        <div className="p-4">
          {packNote && (
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">{packNote}</p>
          )}
          {checkErr && (
            <p className="text-sm text-red-600 dark:text-red-400 mb-3">{checkErr}</p>
          )}

          {!reach && !checking && (
            <p className="text-sm text-gray-500">
              Nothing has been checked yet. The check reads every pupil's contact address, resolves
              each distinct domain once, and reports which families a reminder can actually get to.
              It changes nothing.
            </p>
          )}

          {checking && <div className="py-6 flex justify-center"><Spinner label="Resolving domains…" /></div>}

          {rep && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <StatChip tone="emerald" value={String(rep.reachable.pupils)}
                  label={`Reachable today · ${money(rep.reachable.outstanding)} owed`} />
                <StatChip tone={rep.recoverable.pupils ? 'amber' : 'slate'}
                  value={String(rep.recoverable.pupils)}
                  label={`Address on file is dead, a working one exists · ${money(rep.recoverable.outstanding)}`} />
                <StatChip tone={rep.unreachable.pupils ? 'rose' : 'slate'}
                  value={String(rep.unreachable.pupils)}
                  label={`Nothing usable anywhere · ${money(rep.unreachable.outstanding)}`} />
              </div>

              {reach!.deadDomains.length > 0 && (
                <div className="mb-4 text-[12px] text-gray-600 dark:text-gray-300">
                  <span className="font-medium">Domains that cannot receive mail: </span>
                  {reach!.deadDomains.map((d) => (
                    <span key={d.domain}
                      className="inline-block font-mono px-2 py-0.5 mr-1.5 rounded bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200">
                      {d.domain} ({d.pupils})
                    </span>
                  ))}
                </div>
              )}

              {/* EVIDENCE, NOT INFERENCE. A dead domain is worked out from a
                  missing MX record. These are addresses a mail server was
                  handed the message and refused — which is the only way to
                  catch a typo on a domain that answers perfectly well. */}
              {(reach!.bouncedAddresses ?? 0) > 0 && (
                <div className="rounded-lg border border-rose-200 dark:border-rose-900/60 bg-rose-50/60 dark:bg-rose-950/20 p-3 mb-4">
                  <p className="text-sm font-medium text-rose-900 dark:text-rose-200 mb-1">
                    {reach!.bouncedAddresses} address{reach!.bouncedAddresses === 1 ? '' : 'es'} have
                    been refused by the receiving mail server.
                  </p>
                  <p className="text-[12px] text-gray-600 dark:text-gray-300">
                    Not a guess from the domain — a real bounce came back, and it is in
                    Communications → School Mailbox with the reason. Several are simple
                    typing errors on domains that work perfectly well, so a domain check
                    would never find them. Reminders now SKIP these until the address is
                    corrected: sending again only produces another bounce and another
                    &ldquo;sent&rdquo; that never arrived.
                  </p>
                </div>
              )}

              {reach!.recoverableTotal > 0 && (
                <div className="rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-950/20 p-3 mb-4">
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-200 mb-1">
                    {reach!.recoverableTotal} of these families already gave the school a working
                    address.
                  </p>
                  <p className="text-[12px] text-gray-600 dark:text-gray-300">
                    It is on their admissions record, in the guardian details they filled in when
                    they applied. The reminder is being sent to a different field. Nobody needs to
                    collect anything — but repointing the contact address for
                    {' '}{reach!.recoverableTotal} pupils is a deliberate decision, so it is not
                    done from this screen. Export the list, check it, and it can be applied in one
                    run.
                  </p>
                </div>
              )}

              {reach!.recoverable.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                        <th className="py-2 pr-2 font-semibold">Pupil</th>
                        <th className="px-2 font-semibold">Being sent to</th>
                        <th className="px-2 font-semibold">Address that works</th>
                        <th className="px-2 pr-2 font-semibold text-right">Outstanding</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reach!.recoverable.slice(0, 50).map((r) => (
                        <tr key={r.studentId} className="border-b border-gray-50 dark:border-gray-800">
                          <td className="py-2 pr-2">
                            {r.studentName}
                            <span className="block text-[11px] text-gray-400">{r.className}</span>
                          </td>
                          <td className="px-2">
                            <s className="font-mono text-xs text-red-700 dark:text-red-300">{r.current}</s>
                          </td>
                          <td className="px-2">
                            <span className="font-mono text-xs text-emerald-700 dark:text-emerald-300">{r.email}</span>
                            <span className="block text-[11px] text-gray-400">{r.who}</span>
                          </td>
                          <td className="px-2 pr-2 text-right tabular-nums">{money(r.outstanding)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {reach!.recoverableTotal > 50 && (
                    <p className="text-[12px] text-gray-500 p-3">
                      and {reach!.recoverableTotal - 50} more — the full list is in the CSV.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </SoftCard>

      {/* ---- did the sweep run --------------------------------- */}
      <SoftCard pad="p-0">
        <PanelHeader icon={Inbox} tone="navy" title="Reminders by day"
          subtitle="A scheduled job that stops running says nothing — this is where that shows" />
        {days.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">Nothing has been sent yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                  <th className="py-2 pl-4 pr-2 font-semibold">Day</th>
                  <th className="px-2 font-semibold text-right">Attempted</th>
                  <th className="px-2 pr-4 font-semibold text-right">Accepted</th>
                </tr>
              </thead>
              <tbody>
                {days.map((d) => (
                  <tr key={d.day} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="py-2 pl-4 pr-2 tabular-nums">{d.day}</td>
                    <td className="px-2 text-right tabular-nums">{d.attempted}</td>
                    <td className={`px-2 pr-4 text-right tabular-nums ${
                      d.accepted < d.attempted ? 'text-amber-700 dark:text-amber-300' : ''
                    }`}>{d.accepted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SoftCard>

      {/* ---- the work list ------------------------------------- */}
      <SoftCard pad="p-0">
        <PanelHeader
          icon={MailX} tone="gold" title="Reminders that did not get through"
          subtitle="Refusals first, then send failures, then families with no address at all"
          right={(
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Pupil or address…"
                className={glassFieldClass('max-w-[200px] text-xs pl-8')} />
            </div>
          )}
        />
        {trouble.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">
            {log.length === 0
              ? 'Nothing has been sent yet.'
              : 'Every reminder in the log was accepted — which is not the same as delivered.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                  <th className="py-2 pl-4 pr-2 font-semibold">Pupil</th>
                  <th className="px-2 font-semibold">Sent to</th>
                  <th className="px-2 font-semibold">What happened</th>
                  <th className="px-2 font-semibold">When</th>
                  <th className="px-2 pr-4 font-semibold">By</th>
                </tr>
              </thead>
              <tbody>
                {trouble.slice(0, 200).map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="py-2 pl-4 pr-2">
                      {r.studentName || r.studentId}
                      {r.invoiceNo && (
                        <span className="block text-[11px] font-mono text-gray-400">{r.invoiceNo}</span>
                      )}
                    </td>
                    <td className="px-2 font-mono text-xs text-gray-600 dark:text-gray-300">
                      {r.to || <span className="italic text-gray-400">none</span>}
                    </td>
                    <td className="px-2">
                      <span className={`text-xs font-medium ${
                        r.outcome === 'rejected' ? 'text-red-700 dark:text-red-300'
                          : r.outcome === 'failed' ? 'text-amber-700 dark:text-amber-300'
                            : 'text-gray-500'
                      }`}>{OUTCOME_LABEL[r.outcome ?? ''] ?? r.outcome}</span>
                      {r.reason && (
                        <span className="block text-[11px] text-gray-400">{r.reason}</span>
                      )}
                    </td>
                    <td className="px-2 text-xs text-gray-500 tabular-nums">{when(r.atIso ?? '')}</td>
                    <td className="px-2 pr-4 text-xs text-gray-500">
                      {r.by === 'scheduled' ? 'Nightly sweep' : 'Sent by hand'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {trouble.length > 200 && (
              <p className="text-[12px] text-gray-500 p-3">and {trouble.length - 200} more.</p>
            )}
          </div>
        )}
      </SoftCard>
    </div>
  );
}
